import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';
import {
  KeyRound, Palette, UserPlus, HelpCircle, BarChart3,
  FileText, Building2, Users, CreditCard, Gavel, Mail, Contact,
} from 'lucide-react';

// ─── Horse Icon ───────────────────────────────────────────────────────────────
const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4 L6 10 C6 14.4 8.7 18 12 18 C15.3 18 18 14.4 18 10 L18 4" />
  </svg>
);

// ─── Checklist definitions per role ─────────────────────────────────────────
const CHECKLISTS = {
  system_owner: [
    { key: 'update_password',  label: 'Update your password',        description: 'Set a secure password for your account.',  path: '/profile?tab=security',    icon: KeyRound },
    { key: 'configure_email',  label: 'Configure email settings',    description: 'Set up SMTP so Green Door can send emails.',path: '/admin/config?tab=system', icon: Mail },
    { key: 'view_tenants',     label: 'View your tenants',           description: 'Review the tenants on your platform.',      path: '/admin/system',            icon: Building2 },
  ],
  tenant_system_admin: [
    { key: 'update_password',   label: 'Update your password',       description: 'Set a secure password for your account.',      path: '/profile?tab=security',     icon: KeyRound },
    { key: 'setup_branding',    label: 'Set up company details',     description: 'Add your company info and upload your logo.',   path: '/tenant-admin?tab=details', icon: Palette },
    { key: 'invite_user',       label: 'Invite a team member',       description: 'Add your first user to the platform.',          path: '/tenant-admin?tab=users',   icon: UserPlus },
    { key: 'add_horse',         label: 'Add your first horse',       description: 'Start building your virtual stable.',           path: '/horses',                   icon: HorseIcon },
    { key: 'add_contact',       label: 'Add an Account',             description: 'Start building your client rolodex.',           path: '/crm/accounts',             icon: Users },
    { key: 'add_sale',          label: 'Add a Sale',                 description: 'Create a new sale (Keeneland, OBS, etc.).',     path: '/sales',                    icon: Gavel },
    { key: 'create_sale_entry', label: 'Create a sale entry',        description: 'Enter a horse into an upcoming sale.',          path: '/sales/opportunities',      icon: Gavel },
  ],
  tenant_acct_admin: [
    { key: 'update_password',   label: 'Update your password',       description: 'Set a secure password for your account.',  path: '/profile?tab=security',   icon: KeyRound },
    { key: 'add_horse',         label: 'Add your first horse',       description: 'Start building your virtual stable.',       path: '/horses',                 icon: HorseIcon },
    { key: 'add_contact',       label: 'Add an Account',             description: 'Start building your client rolodex.',       path: '/crm/accounts',           icon: Users },
    { key: 'add_sale',          label: 'Add a Sale',                 description: 'Create a new sale (Keeneland, OBS, etc.)',  path: '/sales',                  icon: Gavel },
    { key: 'create_sale_entry', label: 'Create a sale entry',        description: 'Enter a horse into an upcoming sale.',      path: '/sales/opportunities',    icon: Gavel },
    { key: 'create_invoice',    label: 'Create an invoice',          description: 'Bill a client for commission or proceeds.', path: '/invoices',               icon: FileText },
  ],
  tenant_acct_manager: [
    { key: 'update_password',   label: 'Update your password',       description: 'Set a secure password for your account.',  path: '/profile?tab=security',   icon: KeyRound },
    { key: 'add_horse',         label: 'Add your first horse',       description: 'Start building your virtual stable.',       path: '/horses',                 icon: HorseIcon },
    { key: 'add_contact',       label: 'Add an Account',             description: 'Start building your client rolodex.',       path: '/crm/accounts',           icon: Users },
    { key: 'add_sale',          label: 'Add a Sale',                 description: 'Create a new sale (Keeneland, OBS, etc.)',  path: '/sales',                  icon: Gavel },
    { key: 'create_sale_entry', label: 'Create a sale entry',        description: 'Enter a horse into an upcoming sale.',      path: '/sales/opportunities',    icon: Gavel },
    { key: 'create_invoice',    label: 'Create an invoice',          description: 'Bill a client for commission or proceeds.', path: '/invoices',               icon: FileText },
  ],
  tenant_team_member: [
    { key: 'update_password', label: 'Update your password',       description: 'Set a secure password for your account.',      path: '/profile?tab=security', icon: KeyRound },
    { key: 'browse_horses',   label: 'Browse the horse stable',    description: 'Explore the horses in your stable.',            path: '/horses',               icon: HorseIcon },
    { key: 'view_crm',        label: 'View an Account',            description: 'Explore client accounts and contacts.',         path: '/crm/accounts',         icon: Users },
    { key: 'check_tasks',     label: 'Check your tasks',           description: 'View tasks and activities assigned to you.',    path: '/tasks',                icon: HelpCircle },
    { key: 'add_sale',        label: 'Add a Sale',                 description: 'Create a new sale (Keeneland, OBS, etc.).',     path: '/sales',                icon: Gavel },
    { key: 'create_sale_entry', label: 'Create a sale entry',      description: 'Enter a horse into an upcoming sale.',          path: '/sales/opportunities',  icon: Gavel },
  ],
  tenant_team_leader: [
    { key: 'update_password',  label: 'Update your password',      description: 'Set a secure password for your account.',      path: '/profile?tab=security', icon: KeyRound },
    { key: 'browse_horses',    label: 'Browse the horse stable',   description: 'Explore the horses in your stable.',            path: '/horses',               icon: HorseIcon },
    { key: 'add_contact',      label: 'Add an Account',            description: 'Start building your client rolodex.',           path: '/crm/accounts',         icon: Users },
    { key: 'check_tasks',      label: 'View team activity',        description: "Check tasks and events for your team.",         path: '/tasks',                icon: Users },
    { key: 'explore_reports',  label: 'Explore reports',           description: 'Run and review sale performance reports.',      path: '/reports',              icon: BarChart3 },
    { key: 'add_sale',         label: 'Add a Sale',                description: 'Create a new sale (Keeneland, OBS, etc.).',     path: '/sales',                icon: Gavel },
    { key: 'create_sale_entry', label: 'Create a sale entry',      description: 'Enter a horse into an upcoming sale.',          path: '/sales/opportunities',  icon: Gavel },
  ],
  tenant_card_runner: [
    { key: 'update_password', label: 'Update your password',       description: 'Set a secure password for your account.',      path: '/profile?tab=security',  icon: KeyRound },
    { key: 'view_cards',      label: 'View sale viewing cards',    description: 'Check buyer viewing cards for the current sale.', path: '/card-management',    icon: CreditCard },
  ],
  tenant_sale_clerk: [
    { key: 'update_password', label: 'Update your password',       description: 'Set a secure password for your account.',      path: '/profile?tab=security', icon: KeyRound },
    { key: 'view_sales',      label: 'View active sales',          description: 'Check the sales calendar and active entries.', path: '/sales',                icon: Gavel },
  ],
  tenant_other: [
    { key: 'update_password', label: 'Update your password',       description: 'Set a secure password for your account.',      path: '/profile?tab=security', icon: KeyRound },
    { key: 'browse_horses',   label: 'Browse the horse stable',    description: 'Explore the horses in your stable.',            path: '/horses',               icon: HorseIcon },
    { key: 'view_crm',        label: 'View an Account',            description: 'Explore client accounts in the CRM.',           path: '/crm/accounts',         icon: Users },
    { key: 'find_contact',    label: 'Find a Contact',             description: 'Search and browse individual contacts.',        path: '/crm/contacts',         icon: Contact },
    { key: 'explore_reports', label: 'Explore reports',            description: 'Run and review sale performance reports.',      path: '/reports',              icon: BarChart3 },
  ],
  owner: [
    { key: 'update_password',  label: 'Update your password',      description: 'Set a secure password for your account.',      path: '/profile?tab=security',       icon: KeyRound },
    { key: 'view_horses',      label: 'View your horses',           description: 'See all your registered horses.',              path: '/owner-portal',               icon: HorseIcon },
    { key: 'check_documents',  label: 'Check your documents',       description: 'Access invoices, reports, and contracts.',     path: '/owner-portal?tab=documents', icon: FileText },
    { key: 'view_sales',       label: 'View sale activity',         description: 'Review your active sale entries.',             path: '/owner-portal?tab=sales',     icon: Gavel },
  ],
};

// Items that auto-complete when user visits their target path
const AUTO_COMPLETE_MAP = {
  browse_horses:    (path) => path.startsWith('/horses'),
  view_crm:         (path) => path.startsWith('/crm/accounts'),
  find_contact:     (path) => path.startsWith('/crm/contacts'),
  check_tasks:      (path) => path.startsWith('/tasks') || path.startsWith('/activities'),
  explore_reports:  (path) => path.startsWith('/reports'),
  view_tenants:     (path) => path.startsWith('/admin/system'),
  review_pricing:   (path) => path.startsWith('/admin/config'),
  configure_email:  (path) => path.startsWith('/admin/config'),
  view_cards:       (path) => path.startsWith('/card-management'),
  view_sales:       (path) => path.startsWith('/sales') || path.startsWith('/owner-portal'),
  add_sale:         (path) => path.startsWith('/sales'),
  add_horse:        (path) => path === '/horses/new' || path.startsWith('/horses/new'),
  add_contact:      (path) => path === '/crm/accounts/new' || path.startsWith('/crm/accounts/new'),
  create_sale_entry:(path) => path === '/sales/opportunities/new' || path.startsWith('/sales/opportunities/new'),
  view_horses:      (path) => path.startsWith('/owner-portal'),
  check_documents:  (path) => path.startsWith('/owner-portal'),
};

// ─── Context ─────────────────────────────────────────────────────────────────
const OnboardingContext = createContext(null);

export const OnboardingProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [completed, setCompleted] = useState(new Set());
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Derive primary role
  const getPrimaryRole = useCallback(() => {
    if (!user) return null;
    const roles = user.roles || [];
    if (roles.includes('system_owner'))         return 'system_owner';
    if (roles.includes('tenant_system_admin'))  return 'tenant_system_admin';
    if (roles.includes('tenant_acct_admin'))    return 'tenant_acct_admin';
    if (roles.includes('tenant_acct_manager'))  return 'tenant_acct_manager';
    if (roles.includes('tenant_team_member'))   return 'tenant_team_member';
    if (roles.includes('tenant_team_leader'))   return 'tenant_team_leader';
    if (roles.includes('tenant_card_runner'))   return 'tenant_card_runner';
    if (roles.includes('tenant_sale_clerk'))    return 'tenant_sale_clerk';
    if (roles.includes('tenant_other'))         return 'tenant_other';
    if (roles.includes('owner'))               return 'owner';
    return null;
  }, [user]);

  const checklist = CHECKLISTS[getPrimaryRole()] || [];

  // Load completed items from backend + persist dismiss state in localStorage
  useEffect(() => {
    if (!user) return;
    const dismissKey = `onboarding_dismissed_${user.id}`;
    if (localStorage.getItem(dismissKey) === 'true') {
      setDismissed(true);
      setLoaded(true);
      return;
    }
    api.get('/onboarding/status')
      .then(r => { setCompleted(new Set(r.data.completed || [])); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [user?.id]);

  // Auto-complete on page visit
  useEffect(() => {
    if (!loaded || !checklist.length) return;
    const path = location.pathname;
    checklist.forEach(item => {
      const autoFn = AUTO_COMPLETE_MAP[item.key];
      if (autoFn && autoFn(path) && !completed.has(item.key)) {
        completeItem(item.key);
      }
    });
  }, [location.pathname, loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const completeItem = useCallback((key) => {
    setCompleted(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    api.post(`/onboarding/complete/${key}`).catch(() => {});
  }, []);

  const dismiss = useCallback(() => {
    if (user?.id) localStorage.setItem(`onboarding_dismissed_${user.id}`, 'true');
    setDismissed(true);
  }, [user?.id]);

  const isComplete = useCallback((key) => completed.has(key), [completed]);
  // Returns false when dismissed so beacons also disappear
  const isInChecklist = useCallback((key) => !dismissed && checklist.some(i => i.key === key), [checklist, dismissed]);

  const doneCount = checklist.filter(i => completed.has(i.key)).length;
  const allDone = checklist.length > 0 && doneCount === checklist.length;

  return (
    <OnboardingContext.Provider value={{
      checklist, completed, completeItem, isComplete, isInChecklist,
      doneCount, allDone, loaded, dismissed, dismiss,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) return {
    checklist: [], completed: new Set(), completeItem: () => {},
    isComplete: () => true, isInChecklist: () => false,
    doneCount: 0, allDone: true, loaded: false, dismissed: false, dismiss: () => {},
  };
  return ctx;
};
